import { CommonModule } from '@angular/common';
import {
    ChangeDetectionStrategy,
    ChangeDetectorRef,
    Component,
    Inject,
    Input,
    OnChanges,
    OnDestroy,
    OnInit,
    SimpleChanges,
} from '@angular/core';
import { Subject } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';
import { WishlistService } from 'src/app/services/wishlist.service';
import {environment} from "../../../../environments/environment";
import { HOTEL_TOURS_PAGE_CONFIG, HotelToursPageConfig } from '../../../config/hotel-tours-list-page.config';
import { HTTPSearchResultTour } from '../../../models';
import { WindowRefService } from '../../../services/window-ref.service';
import { PopupState, WishlistHotelPopupService } from '../../../services/wishlist-hotel-popup.service';
import { HotelWishlistPopupComponent } from '../hotel-wishlist-popup/hotel-wishlist-popup.component';


@Component({
    selector: 'app-hotel-wishlist',
    templateUrl: environment.mobile ? './hotel-wishlist.component.mobile.html' : './hotel-wishlist.component.html',
    styleUrls: environment.mobile ? ['./hotel-wishlist.component.mobile.scss'] : ['./hotel-wishlist.component.scss'],
    imports: [
        CommonModule,
        HotelWishlistPopupComponent,
    ],
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
})
export class HotelWishlistComponent implements OnInit, OnDestroy, OnChanges {
    @Input() tour: HTTPSearchResultTour;
    @Input() wishlistHotels = [];
    isLike = false;
    isLoading = false; // Флаг для предотвращения повторных запросов
    animateClass = '';
    popupState: PopupState = {isVisible: false, isAdded: false, hotelId: null};

    private destroy$ = new Subject<void>();

    constructor(
        @Inject(HOTEL_TOURS_PAGE_CONFIG) private appConfig: HotelToursPageConfig,
        private window: WindowRefService,
        private wishlistService: WishlistService,
        private hotelWishlistPopupService: WishlistHotelPopupService,
        private cdr: ChangeDetectorRef,
    ) {
        this.hotelWishlistPopupService.popupState$
            .pipe(takeUntil(this.destroy$))
            .subscribe(state => {
                this.popupState = state;
                this.cdr.detectChanges();
            });
    }

    ngOnInit() {
        this.updateIsLike();
        this.showPopupAfterAuthRedirect();
    }

    ngOnChanges(changes: SimpleChanges) {
        if (changes['wishlistHotels']) {
            this.updateIsLike();
        }
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    private updateIsLike(): void {
        const isHotelInWishlist = this.wishlistHotels.includes(this.tour.hotelId);
        this.isLike = !!isHotelInWishlist;
    }

    triggerBtn(): void {
        if (!this.isClientAuth) {
            this.sendWishlistHotelAnalytics('click_add_wishlist_hotel');
            this.openWishlistAuthModal();
            return;
        }

        this.isLike ? this.removeFromWishlist() : this.addToWishlist();
    }

    addToWishlist(): void {
        if (!this.appConfig.isClientAuth) {
            this.openWishlistAuthModal();
            return;
        }

        if (this.isLoading) return;

        this.isLoading = true;
        this.triggerAnimation();

        this.wishlistService
            .addHotelToWishlist(this.tour.hotelId)
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    this.isLike = true;
                    this.isLoading = false;
                    this.hotelWishlistPopupService.showPopup(true, this.tour.hotelId);
                    this.sendWishlistHotelAnalytics('click_add_wishlist_hotel');

                    this.cdr.detectChanges();
                })
            )
            .subscribe({
                error: (error) => {
                    this.handleError(error);
                },
            });
    }

    removeFromWishlist(): void {
        if (!this.appConfig.isClientAuth) {
            this.openWishlistAuthModal();
            return;
        }

        if (this.isLoading) return;

        this.isLoading = true;
        this.triggerAnimation();

        this.wishlistService
            .removeHotelFromWishlist(this.tour.hotelId)
            .pipe(
                takeUntil(this.destroy$),
                tap(() => {
                    this.isLike = false;
                    this.isLoading = false;
                    this.hotelWishlistPopupService.showPopup(false, this.tour.hotelId);
                    this.sendWishlistHotelAnalytics('click_remove_wishlist_hotel');
                    this.cdr.detectChanges();
                })
            )
            .subscribe({
                error: (error) => {
                    this.handleError(error);
                },
            });
    }

    private sendWishlistHotelAnalytics(eventName) {
        const eventProperties: any = Object.assign({});
        eventProperties.hotelId = this.tour.hotelId;
        eventProperties.place = 'client_search_page';
        this.window.nativeWindow.brxTracking.sendAnalyticsEvent(eventName, eventProperties);
    }

    private handleError(error: any): void {
        console.error('Ошибка:', error);
        if (error?.status === 500) {
            this.openWishlistAuthModal();
        }
        this.isLoading = false;
        this.cdr.detectChanges();
    }

    triggerAnimation(): void {
        this.animateClass = 'pulse-animation';
        setTimeout(() => {
            this.animateClass = '';
            this.cdr.detectChanges();
        }, 1000);
    }

    openWishlistAuthModal() {
        const nativeWindow = this.window.nativeWindow as any;
        const modal = nativeWindow.modal;

        if (modal?.open) {
            nativeWindow.sessionStorage.setItem('wishlistAuthHotelId', String(this.tour.hotelId));
            nativeWindow.sessionStorage.removeItem('wishlistAuthSuccessHotelId');

            if (nativeWindow.authWidget?.links) {
                nativeWindow.authWidget.links.afterLoginRedirectLink = nativeWindow.location.href;
            }

            modal.open({
                type: modal.types?.BOTTOM || 'bottom',
                parentCssClass: environment.mobile ? 'bottom-scheat-auth' : 'desktop-auth-modal',
                header: {
                    title: nativeWindow.authWidget?.labels?.title || 'Вход/Регистрация',
                },
                content: {
                    htmlDetachSelectorId: 'authModal',
                },
                footer: false,
                onBeforeClose: () => {
                    nativeWindow.sessionStorage.removeItem('wishlistAuthHotelId');
                },
                onAfterShow: () => {
                    if (typeof nativeWindow.authWidget?.sendOpenEvent === 'function') {
                        nativeWindow.authWidget.sendOpenEvent();
                    }
                },
            });
            return;
        }

        this.window.nativeWindow.location.href = this.wishlistLoginLink;
    }

    getButtonClasses(): string[] {
        const classes = [];

        if (this.animateClass) {
            classes.push(this.animateClass);
        }

        return classes;
    }

    get isClientAuth(): boolean {
        return this.appConfig.isClientAuth;
    }

    get wishlistLoginLink(): string {
        return `/auth/login?fromHotelWishlist=1${this.tour?.hotelId ? `&hotelId=${this.tour.hotelId}` : ''}`;
    }

    get isPopupVisible(): boolean {
        return this.popupState.isVisible && this.popupState.hotelId === this.tour.hotelId;
    }

    private showPopupAfterAuthRedirect(): void {
        const nativeWindow = this.window.nativeWindow as any;
        const storedHotelId = nativeWindow.sessionStorage.getItem('wishlistAuthSuccessHotelId');

        if (!storedHotelId || Number(storedHotelId) !== this.tour.hotelId) {
            return;
        }

        nativeWindow.sessionStorage.removeItem('wishlistAuthSuccessHotelId');
        this.isLike = true;
        this.hotelWishlistPopupService.showPopup(true, this.tour.hotelId);
        this.cdr.detectChanges();
    }
}
